import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FetchPayouts } from "../../apis/Payout";
import {
  getPayoutStatusBadge,
  formatCurrency,
  formatDate,
} from "./payoutHelpers";

const STATUSES = [
  "all",
  "pending",
  "processing",
  "completed",
  "failed",
  "cancelled",
];

const PayoutHistory = () => {
  const [payouts, setPayouts] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const limit = "10";

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const status = statusFilter === "all" ? "" : statusFilter;
      const res = await FetchPayouts(currentPage, limit, status);
      if (res.success) {
        setPayouts(res.data?.payouts || []);
        setTotalPages(res.data?.totalPages?.toString() || "1");
      }
    } catch (error) {
      toast.error("Failed to fetch payouts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  useEffect(() => {
    setCurrentPage("1");
  }, [statusFilter]);

  const columns = useMemo(
    () => [
      "Payout ID",
      "Host",
      "Net Amount",
      "Status",
      "Bank Account",
      "Created",
      "Paid At",
      "Actions",
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <Link
                to="/payouts"
                className="text-secondaryColor hover:underline text-sm"
              >
                ← Back to Dashboard
              </Link>
              <h1 className="text-2xl dark:text-[#c7d8eb]">Payout History</h1>
            </div>

            {/* Status Filter */}
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1 rounded text-xs font-semibold capitalize border ${
                        statusFilter === status
                          ? "bg-secondaryColor text-white border-secondaryColor"
                          : "bg-[#ffffff] dark:bg-darkBg2 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-secondaryColor"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto w-full">
                {/* Desktop Table */}
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0
                          ? "w-[12%]"
                          : index === 1
                            ? "w-[15%]"
                            : index === 2
                              ? "w-[12%]"
                              : index === 3
                                ? "w-[10%]"
                                : index === 4
                                  ? "w-[15%]"
                                  : index === 5
                                    ? "w-[12%]"
                                    : index === 6
                                      ? "w-[12%]"
                                      : "w-[12%]"
                      } min-w-[110px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold text-xs`}
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                  </div>
                ) : payouts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No payouts found.
                  </div>
                ) : (
                  payouts.map((payout, index) => (
                    <React.Fragment key={`payout-${index}`}>
                      {/* Desktop Row */}
                      <div className="hidden lg:flex w-full">
                        <div className="w-[12%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs font-mono">
                          {payout._id?.slice(-8)}
                        </div>
                        <div className="w-[15%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {payout.host?.firstName} {payout.host?.lastName}
                        </div>
                        <div className="w-[12%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs font-semibold">
                          {formatCurrency(payout.netAmount)}
                        </div>
                        <div className="w-[10%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${getPayoutStatusBadge(
                              payout.status,
                            )}`}
                          >
                            {payout.status}
                          </span>
                        </div>
                        <div className="w-[15%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {payout.bankAccount?.IbanNumber
                            ? `${payout.bankAccount.bankName || ""} - ${payout.bankAccount.IbanNumber.slice(-6)}`
                            : "-"}
                        </div>
                        <div className="w-[12%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {formatDate(payout.createdAt)}
                        </div>
                        <div className="w-[12%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center text-xs">
                          {formatDate(payout.paidAt)}
                        </div>
                        <div className="w-[12%] min-w-[110px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <Link
                            to={`/payouts/detail/${payout._id}`}
                            className="text-secondaryColor text-xs hover:underline"
                          >
                            View
                          </Link>
                        </div>
                      </div>

                      {/* Mobile Card */}
                      <div className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white">
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Payout ID</div>
                          <div className="font-mono text-sm">
                            {payout._id?.slice(-8)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host</div>
                          <div className="text-sm">
                            {payout.host?.firstName} {payout.host?.lastName}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Net Amount</div>
                          <div className="font-semibold text-sm">
                            {formatCurrency(payout.netAmount)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Status</div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${getPayoutStatusBadge(
                              payout.status,
                            )}`}
                          >
                            {payout.status}
                          </span>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Bank</div>
                          <div className="text-sm">
                            {payout.bankAccount?.IbanNumber
                              ? `${payout.bankAccount.bankName || ""} - ${payout.bankAccount.IbanNumber.slice(-6)}`
                              : "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Created</div>
                          <div className="text-sm">
                            {formatDate(payout.createdAt)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Paid At</div>
                          <div className="text-sm">
                            {formatDate(payout.paidAt)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Actions</div>
                          <Link
                            to={`/payouts/detail/${payout._id}`}
                            className="text-secondaryColor text-sm hover:underline"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayoutHistory;
